export interface ParseResult<T = any> {
	data: T[];
	errors: string[];
	warnings: string[];
	totalRecords: number;
	validRecords: number;
}

export const parseJsonData = <T = any>(jsonString: string): ParseResult<T> => {
	const result: ParseResult<T> = {
		data: [],
		errors: [],
		warnings: [],
		totalRecords: 0,
		validRecords: 0
	};

	if (!jsonString.trim()) {
		result.errors.push('Input data is empty');
		return result;
	}

	try {
		const parsed = JSON.parse(jsonString);
		
		// Handle both single objects and arrays
		if (Array.isArray(parsed)) {
			result.data = parsed;
			result.totalRecords = parsed.length;
		} else if (typeof parsed === 'object' && parsed !== null) {
			result.data = [parsed];
			result.totalRecords = 1;
		} else {
			result.errors.push('JSON must contain an object or array of objects');
			return result;
		}

		// Validate each record
		result.data.forEach((record, index) => {
			if (typeof record !== 'object' || record === null) {
				result.errors.push(`Record ${index + 1}: Must be an object`);
			} else {
				result.validRecords++;
			}
		});

		if (result.validRecords === 0) {
			result.errors.push('No valid records found');
		}

	} catch(error) {
		result.errors.push(`JSON parsing error: ${error instanceof Error ? error.message : 'Invalid JSON format'}`);
	}

	return result;
};


export const validateDataFormat = (data: any[], resourceType: string = 'market'): string[] => {
	const errors: string[] = [];

	if (!Array.isArray(data)) {
		errors.push('Data must be an array of records');
		return errors;
	}

	if (data.length === 0) {
		errors.push('No data records to validate');
		return errors;
	}

	// Market-specific validation
	if (resourceType === 'market') {
		const requiredFields = ['id', 'linkName', 'linkTitle', 'queryString'];
		const booleanFields = ['featured'];
		
		data.forEach((record, index) => {
			const recordNum = index + 1;
			
			// Check required fields
			requiredFields.forEach(field => {
				if (!record[field] || String(record[field]).trim() === '') {
					errors.push(`Record ${recordNum}: Missing required field '${field}'`);
				}
			});

			// Validate queryString format
			if (record.queryString) {
				const queryString = String(record.queryString);
				if (!queryString.includes('=')) {
					errors.push(`Record ${recordNum}: queryString appears to be invalid (no key=value pairs found)`);
				}
				
				// Check for common query string patterns
				if (!queryString.match(/page=|pt=|city\[\]=|idxStatus=/)) {
					errors.push(`Record ${recordNum}: queryString may be missing common search parameters`);
				}
			}

			// Validate featured field
			if (record.featured && !['y', 'n', 'yes', 'no', 'true', 'false', '1', '0'].includes(String(record.featured).toLowerCase())) {
				errors.push(`Record ${recordNum}: 'featured' field must be 'y', 'n', or boolean equivalent`);
			}

			// Validate ID field is numeric or string
			if (record.id && isNaN(Number(record.id))) {
				// ID can be string, just warn if it's not numeric
			}

			// Check for extremely long queryString (potential data issue)
			if (record.queryString && String(record.queryString).length > 2000) {
				errors.push(`Record ${recordNum}: queryString is unusually long (${String(record.queryString).length} characters) - please verify`);
			}
		});
	}

	return errors;
};

export const detectInputFormat = (input: string): 'json' | 'unknown' => {
	if (!input.trim()) {
		return 'unknown';
	}

	const trimmed = input.trim();
	
	// Check for JSON patterns
	if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
		(trimmed.startsWith('[') && trimmed.endsWith(']'))) {
		try {
			JSON.parse(trimmed);
			return 'json';
		} catch{
			return 'unknown';
		}
	}
	
	return 'unknown';
};